import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Chip, Stack,
} from '@mui/material';
import {
  getFbaCapacityMerchants, getFbaCapacityLimits, getFbaCapacityUtilization, createFbaCapacityLimit,
} from '../../services/inventoryService';
import { useAuth } from '../../context/AuthContext';

const REGIONS = ['NA', 'EU', 'FE'];
const STORAGE_TYPES = ['Standard', 'Oversize', 'Apparel', 'Footwear', 'Flammable', 'Aerosol'];
const thisMonth = () => new Date().toISOString().slice(0, 7) + '-01';

// The utilisation view nullIf()s the LEFT-JOINed limit, so entered_limit is NULL when none was
// entered — but limit_source is a (non-Nullable) String and comes back as '' in that same case.
// "Entered" therefore requires BOTH: a non-null limit AND a non-empty source.
const hasEnteredLimit = (r) => r.entered_limit != null && r.limit_source !== '';

export default function FbaCapacityLimits() {
  // Entering a limit is a company-wide, audited write (POST /api/fba-capacity-limits). Only a
  // company admin may do it, so a non-admin is shown the read-only tables and told why — rather
  // than being offered a dialog whose Save the backend would reject.
  // The login response carries `is_company_admin` (snake) and only GET /api/users/me carries
  // `isCompanyAdmin` (camel); a client-side navigation straight after login only has the former,
  // so accept both — otherwise a genuine admin sees no Enter-limit button until a hard refresh.
  const { user } = useAuth();
  const canEdit = !!(user?.isCompanyAdmin ?? user?.is_company_admin);
  const [merchants, setMerchants] = useState([]);
  const [limits, setLimits] = useState([]);
  const [util, setUtil] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [form, setForm] = useState({ merchant_id: '', region: 'NA', storage_type: 'Standard', period_month: thisMonth(),
                                     capacity_limit_cubic_feet: '', estimated_units: '', source: 'capacity_monitor' });

  // `isCancelled` lets the effect drop a response that arrived after unmount or after the user
  // moved to a newer month, so a slow earlier request can never overwrite the current month's data.
  const load = useCallback(async (isCancelled = () => false) => {
    setError(''); setSaved('');
    try {
      const [m, l, u] = await Promise.all([
        getFbaCapacityMerchants(), getFbaCapacityLimits({ period_month: month }), getFbaCapacityUtilization({ period_month: month }),
      ]);
      if (isCancelled()) return;
      setMerchants(m.data || []); setLimits(l.data || []); setUtil(u.data || []);
    } catch (e) {
      if (isCancelled()) return;
      setError(e.response?.data?.error || e.message);
    }
  }, [month]);

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => { cancelled = true; };
  }, [load]);

  const save = async () => {
    setError(''); setSaved('');
    try {
      await createFbaCapacityLimit({ ...form, period_month: form.period_month,
        capacity_limit_cubic_feet: Number(form.capacity_limit_cubic_feet),
        estimated_units: form.estimated_units === '' ? null : Number(form.estimated_units) });
      setOpen(false);
      await load();   // load() clears `saved`, so announce only once the refreshed tables are in
      setSaved(`Saved ${form.merchant_id} ${form.region} ${form.storage_type} ${form.period_month}`);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">FBA Capacity Limits</Typography>
        <Stack direction="row" spacing={2}>
          <TextField id="fba-cap-month" label="Month" type="month" size="small" value={month.slice(0, 7)}
                     onChange={(e) => { if (!e.target.value) return; setMonth(e.target.value + '-01'); }} InputLabelProps={{ shrink: true }} />
          {canEdit
            ? <Button id="fba-cap-add" variant="contained" onClick={() => { setError(''); setOpen(true); }}>Enter limit</Button>
            : <Typography variant="caption" color="text.secondary" alignSelf="center">Only company admins can enter limits</Typography>}
        </Stack>
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Amazon exposes no API for capacity limits. Read them from Seller Central → FBA dashboard → Capacity Monitor
        (or the monthly email) and enter one row per seller account, region and storage type. Used volume comes from the
        daily inventory planning report; a limit revealed by an overage-fee report is shown when Amazon charged one.
        All volumes here are cubic feet: marketplaces that report cubic metres (EU, AU) are converted at 1 m³ = 35.3147 ft³.
        If Capacity Monitor shows a limit in cubic metres, multiply it by 35.3147 before entering it.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

      <Typography variant="h6" sx={{ mt: 2 }}>Utilisation — {month.slice(0, 7)}</Typography>
      <Paper sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Seller</TableCell><TableCell>Region</TableCell><TableCell>Marketplaces</TableCell><TableCell>Storage type</TableCell>
            <TableCell align="right">Used (cu ft)</TableCell><TableCell align="right">Limit</TableCell><TableCell>Limit source</TableCell><TableCell align="right">Used %</TableCell><TableCell>Planning snapshot</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {util.length === 0 && <TableRow><TableCell colSpan={9}>No planning data for this month.</TableCell></TableRow>}
            {util.map((r) => (
              // one row per (seller, region, storage type) — the view's grain since 2026-09-22
              <TableRow key={`${r.merchant_id}-${r.region}-${r.storage_type}`}>
                <TableCell>{r.merchant_id}</TableCell><TableCell>{r.region}</TableCell><TableCell>{(r.marketplaces || []).join(', ')}</TableCell><TableCell>{r.storage_type}</TableCell>
                <TableCell align="right">
                  {r.used_volume == null ? '—' : Number(r.used_volume).toFixed(1)}
                  {Number(r.excluded_nan_rows) > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.excluded_nan_rows} SKUs with no reported volume
                    </Typography>
                  )}
                  {Number(r.excluded_unknown_unit_rows) > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.excluded_unknown_unit_rows} {Number(r.excluded_unknown_unit_rows) === 1 ? 'SKU' : 'SKUs'} with no volume unit in Amazon's planning report, not counted
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{(hasEnteredLimit(r) || r.amazon_revealed_limit != null) && r.effective_limit != null ? Number(r.effective_limit).toFixed(0) : '—'}</TableCell>
                <TableCell>{hasEnteredLimit(r) ? <Chip size="small" label={r.limit_source || 'entered'} /> : (r.amazon_revealed_limit != null ? <Chip size="small" color="warning" label="overage report" /> : <Chip size="small" variant="outlined" label="not entered" />)}</TableCell>
                <TableCell align="right">{r.utilization_pct == null ? '—' : `${r.utilization_pct}%`}</TableCell>
                <TableCell>{r.latest_snapshot}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6">Entered limits — {month.slice(0, 7)}</Typography>
      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Seller</TableCell><TableCell>Region</TableCell><TableCell>Storage type</TableCell><TableCell align="right">Limit (cu ft)</TableCell>
            <TableCell align="right">Est. units</TableCell><TableCell>Source</TableCell><TableCell>Entered by</TableCell><TableCell>Updated</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {limits.length === 0 && <TableRow><TableCell colSpan={8}>No limits entered for this month.</TableCell></TableRow>}
            {limits.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.merchant_id}</TableCell><TableCell>{r.region}</TableCell><TableCell>{r.storage_type}</TableCell>
                <TableCell align="right">{r.capacity_limit_cubic_feet}</TableCell><TableCell align="right">{r.estimated_units ?? '—'}</TableCell>
                <TableCell>{r.source}</TableCell><TableCell>{r.entered_by_email}</TableCell><TableCell>{r.updated_at}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Enter FBA capacity limit</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField id="fba-cap-merchant" select label="Seller account" value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}>
              {merchants.map((m) => <MenuItem key={m.merchant_id} value={m.merchant_id}>{m.merchant_id} — {(m.connector_names || []).join(', ')}</MenuItem>)}
            </TextField>
            <TextField id="fba-cap-region" select label="Region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
              {REGIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField id="fba-cap-storage" select label="Storage type" value={form.storage_type} onChange={(e) => setForm({ ...form, storage_type: e.target.value })}>
              {STORAGE_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField id="fba-cap-period" label="Month" type="month" value={form.period_month.slice(0, 7)}
                       onChange={(e) => { if (!e.target.value) return; setForm({ ...form, period_month: e.target.value + '-01' }); }} InputLabelProps={{ shrink: true }} />
            <TextField id="fba-cap-limit" label="Capacity limit (cubic feet)" type="number" inputProps={{ min: 0 }} value={form.capacity_limit_cubic_feet}
                       helperText="Capacity Monitor shows cubic metres? Multiply by 35.3147."
                       onChange={(e) => setForm({ ...form, capacity_limit_cubic_feet: e.target.value })} />
            <TextField id="fba-cap-units" label="Estimated units (optional)" type="number" inputProps={{ min: 0 }} value={form.estimated_units}
                       onChange={(e) => setForm({ ...form, estimated_units: e.target.value })} />
            <TextField id="fba-cap-source" select label="Where you read it" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <MenuItem value="capacity_monitor">Capacity Monitor</MenuItem><MenuItem value="email">Monthly email</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button id="fba-cap-cancel" onClick={() => setOpen(false)}>Cancel</Button>
          {canEdit && (
            <Button id="fba-cap-save" variant="contained" onClick={save}
                    disabled={!form.merchant_id || form.capacity_limit_cubic_feet === '' || !(Number(form.capacity_limit_cubic_feet) > 0)
                              || (form.estimated_units !== '' && !(Number(form.estimated_units) >= 0))}>Save</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
